import i18n from '@/i18n/i18n';
import type { ProfileLocationState } from '@/types/profile-navigation';

const PROFILE_BACK_STORAGE_KEY_PREFIX = 'profileBackFrom_';

function profileBackStorageKey(familyId: string): string {
  return `${PROFILE_BACK_STORAGE_KEY_PREFIX}${familyId}`;
}

export type ProfileScreenKind = 'people' | 'pets';

type StoredProfileBack = {
  screen: ProfileScreenKind;
  familyMemberId: string;
  from?: ProfileLocationState['from'];
  originalAnchorId?: string;
};

/**
 * Resolves where the user navigated from (Tree, Members, etc.) using router state
 * or the same sessionStorage fallback used by profile Back buttons.
 */
export function resolveProfileNavigationContext(
  familyId: string | undefined,
  screen: ProfileScreenKind,
  familyMemberId: string | undefined,
  locationState: ProfileLocationState | null | undefined
): ProfileLocationState | undefined {
  if (locationState?.from) {
    return {
      from: locationState.from,
      originalAnchorId: locationState.originalAnchorId
    };
  }

  if (!familyId || !familyMemberId) return undefined;

  try {
    const raw = sessionStorage.getItem(profileBackStorageKey(familyId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredProfileBack | undefined;
    if (parsed?.screen === screen && parsed.familyMemberId === familyMemberId && parsed.from) {
      return { from: parsed.from, originalAnchorId: parsed.originalAnchorId };
    }
  } catch {
    // Ignore storage errors (private mode, parse failures).
  }

  return undefined;
}

export type ProfileBreadcrumbParent = {
  label: string;
  path: string;
};

/** Middle breadcrumb segment for a person/pet profile based on navigation origin. */
export function getProfileBreadcrumbParent(
  familyId: string,
  nav: ProfileLocationState
): ProfileBreadcrumbParent | undefined {
  switch (nav.from) {
    case 'members':
      return { label: i18n.t('nav:familyNav.members'), path: `/families/${familyId}/members` };
    case 'tree': {
      const anchor = nav.originalAnchorId ?? 'user';
      return {
        label: i18n.t('nav:breadcrumb.pathMap.hybrid-pedigree'),
        path: `/families/${familyId}/hybrid-pedigree/${anchor}`
      };
    }
    case 'add-self':
      return { label: i18n.t('nav:familyNav.overview'), path: `/families/${familyId}/overview` };
    default:
      return undefined;
  }
}
